import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { z } from "zod";
import { Input, Label } from "@cieda/djayma-ui";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { AdminForm } from "@/components/admin/shared/AdminForm";
import { MarketingImageEditor } from "@/components/admin/images";
import type { Category, CategoryFormData } from "@/lib/admin-api/categories";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export const categorySchema = z.object({
  name_fr: z.string().min(1, "Le nom est requis").max(200),
  name_wo: z.string().max(200).optional().default(""),
  name_en: z.string().max(200).optional().default(""),
  slug: z
    .string()
    .min(1, "Le slug est requis")
    .max(200)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug invalide (lettres minuscules, chiffres et tirets)"),
  parent_id: z.string().uuid().nullable().optional().default(null),
  image_url: z.string().url("URL invalide").nullable().optional().default(null),
  is_active: z.boolean().default(true),
});

export type CategoryFormValues = z.infer<typeof categorySchema>;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CategoryFormProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (data: CategoryFormData) => void | Promise<void>;
  readonly category?: Category | null;
  readonly parentId?: string | null;
  readonly allCategories: readonly Category[];
}

// ---------------------------------------------------------------------------
// Slug generator (from French name)
// ---------------------------------------------------------------------------

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// ---------------------------------------------------------------------------
// Flatten categories for parent select (max 2 levels up from root)
// ---------------------------------------------------------------------------

function flattenForParentSelect(
  categories: readonly Category[],
  excludeId?: string,
  depth = 0,
  maxDepth = 1,
): readonly { id: string; label: string; depth: number }[] {
  const result: { id: string; label: string; depth: number }[] = [];
  for (const cat of categories) {
    if (cat.id === excludeId) continue;
    const prefix = "\u00A0\u00A0".repeat(depth);
    result.push({ id: cat.id, label: `${prefix}${cat.name_fr}`, depth });
    if (depth < maxDepth && cat.children.length > 0) {
      result.push(
        ...flattenForParentSelect(cat.children, excludeId, depth + 1, maxDepth),
      );
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Inner form fields (needs FormProvider context from AdminForm)
// ---------------------------------------------------------------------------

function CategoryFields({
  parentOptions,
  isEdit,
}: {
  readonly parentOptions: readonly { id: string; label: string; depth: number }[];
  readonly isEdit: boolean;
}) {
  const { t } = useTranslation("admin");
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CategoryFormValues>();

  const nameFr = watch("name_fr");
  const slug = watch("slug");

  // Auto-generate slug from name_fr when creating (not editing)
  useEffect(() => {
    if (!isEdit && nameFr) {
      const auto = generateSlug(nameFr);
      if (!slug || slug === generateSlug(nameFr.slice(0, -1) || "")) {
        setValue("slug", auto, { shouldValidate: true });
      }
    }
  }, [nameFr, isEdit, slug, setValue]);

  return (
    <div className="space-y-4">
      {/* Name FR (required) */}
      <div>
        <Label htmlFor="name_fr">{t("categories.field_name_fr")} *</Label>
        <Input
          id="name_fr"
          {...register("name_fr")}
          placeholder={t("categories.placeholder_name_fr")}
          className="mt-1"
        />
        {errors.name_fr && (
          <p className="mt-1 text-xs text-red-600">{errors.name_fr.message}</p>
        )}
      </div>

      {/* Name WO (optional) */}
      <div>
        <Label htmlFor="name_wo">{t("categories.field_name_wo")}</Label>
        <Input
          id="name_wo"
          {...register("name_wo")}
          placeholder={t("categories.placeholder_name_wo")}
          className="mt-1"
        />
      </div>

      {/* Name EN (optional) */}
      <div>
        <Label htmlFor="name_en">{t("categories.field_name_en")}</Label>
        <Input
          id="name_en"
          {...register("name_en")}
          placeholder={t("categories.placeholder_name_en")}
          className="mt-1"
        />
      </div>

      {/* Slug */}
      <div>
        <Label htmlFor="slug">{t("categories.field_slug")} *</Label>
        <Input
          id="slug"
          {...register("slug")}
          placeholder="ex: produits-laitiers"
          className="mt-1 font-mono text-sm"
        />
        {errors.slug && (
          <p className="mt-1 text-xs text-red-600">{errors.slug.message}</p>
        )}
      </div>

      {/* Parent category */}
      <div>
        <Label htmlFor="parent_id">{t("categories.field_parent")}</Label>
        <select
          id="parent_id"
          {...register("parent_id")}
          className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
        >
          <option value="">{t("categories.no_parent")}</option>
          {parentOptions.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Image upload */}
      <div>
        <MarketingImageEditor
          preset="product"
          value={watch("image_url") || undefined}
          onChange={(url) => setValue("image_url", url)}
          label={t("categories.field_image")}
        />
      </div>

      {/* Active toggle */}
      <div className="flex items-center gap-3">
        <input
          id="is_active"
          type="checkbox"
          {...register("is_active")}
          className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
        />
        <Label htmlFor="is_active" className="cursor-pointer">
          {t("categories.field_active")}
        </Label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CategoryForm({
  open,
  onClose,
  onSubmit,
  category,
  parentId,
  allCategories,
}: CategoryFormProps) {
  const { t } = useTranslation("admin");
  const isEdit = Boolean(category);

  const defaultValues: CategoryFormValues = useMemo(
    () => ({
      name_fr: category?.name_fr ?? "",
      name_wo: category?.name_wo ?? "",
      name_en: category?.name_en ?? "",
      slug: category?.slug ?? "",
      parent_id: category?.parent_id ?? parentId ?? null,
      image_url: category?.image_url ?? null,
      is_active: category?.is_active ?? true,
    }),
    [category, parentId],
  );

  const parentOptions = useMemo(
    () => flattenForParentSelect(allCategories, category?.id),
    [allCategories, category?.id],
  );

  const title = isEdit
    ? t("categories.edit_title")
    : t("categories.create_title");

  const handleSubmit = async (data: CategoryFormValues) => {
    // Clean up: convert empty parent_id to null
    const cleaned: CategoryFormData = {
      ...data,
      parent_id: data.parent_id || null,
      image_url: data.image_url || null,
    };
    await onSubmit(cleaned);
    onClose();
  };

  return (
    <AdminModal open={open} onClose={onClose} title={title} size="md">
      <AdminForm
        schema={categorySchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        submitLabel={isEdit ? t("actions.save") : t("actions.create")}
      >
        <CategoryFields parentOptions={parentOptions} isEdit={isEdit} />
      </AdminForm>
    </AdminModal>
  );
}
